import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Upload, CheckCircle2, ShieldCheck, Sparkles, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';

const API = `${window.location.origin}/api`;
const PACKAGE_PRICE = 1899;

const INCLUSIONS = [
  '1 Complete Tester Kit — 33 scents updated regularly',
  'Auto-add of new scents on every release',
  '2 pcs 50ml signature bottles',
  'Brand support kit (photos, captions, hooks)',
  'Private dealer Messenger group',
  'Exclusive ambassador Discord/FB community',
];

export default function TesterKitCheckoutPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [reference, setReference] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofUrl, setProofUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please log in first');
      navigate('/login');
      return;
    }
    Promise.all([
      axios.get(`${API}/settings`),
      axios.get(`${API}/tester-kit/my-order`, { headers: headers() }).catch(() => ({ data: {} })),
    ]).then(([s, o]) => {
      setSettings(s.data);
      const existing = o.data?.order;
      setOrder(existing);
      if (existing && existing.status === 'paid_pending_approval') {
        navigate('/pending-payment');
      } else if (existing && existing.status === 'approved') {
        navigate('/ambassador');
      }
    }).finally(() => setLoading(false));
  }, [isAuthenticated, navigate]);

  const handleProofUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) {
      toast.error('File too large (max 6MB)');
      return;
    }
    setProofFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API}/tester-kit/upload-proof`, fd, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
      });
      setProofUrl(res.data.url);
      toast.success('Proof uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      setProofFile(null);
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!paymentMethod) { toast.error('Pick a payment method'); return; }
    if (!proofUrl) { toast.error('Upload your payment proof'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tester-kit/order`, {
        payment_method: paymentMethod,
        payment_reference: reference,
        payment_proof_url: proofUrl,
      }, { headers: headers() });
      toast.success("Got it! We'll review your payment within 24hrs.");
      navigate('/pending-payment');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/40 text-sm font-sans">Loading…</div>;

  const methods = Object.entries(settings?.payment_methods || {}).filter(([, m]) => m?.enabled);
  const selected = methods.find(([k]) => k === paymentMethod)?.[1];

  return (
    <main className="min-h-screen bg-[#0A0A0A] py-10 sm:py-16">
      <SEOHead title="Activate Your Ambassador Account" description="Pay your Tester Kit to activate." robots="noindex,nofollow" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <Crown className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.4} />
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-2 font-sans">Activate Your Ambassador Account</p>
          <h1 className="text-3xl sm:text-4xl text-white font-light leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            One step away. <span className="text-[#D4AF37] italic">Get your Tester Kit.</span>
          </h1>
          <p className="text-white/50 text-sm font-sans mt-3 max-w-xl mx-auto">
            Hi {user?.name?.split(' ')[0] || 'Ambassador'}! Your account is created. To unlock your dashboard, leaderboard, referral link, and earn commissions, complete your Tester Kit purchase below.
          </p>
        </div>

        {/* Package summary */}
        <section className="bg-[#141414] border border-[#D4AF37]/30 p-6 sm:p-8 mb-6" data-testid="kit-summary">
          <div className="flex items-start justify-between gap-4 mb-5 pb-5 border-b border-white/5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans">JS Fragrance™</p>
              <h2 className="text-xl text-white font-medium mt-1 font-sans">Brand Ambassador Package</h2>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-sans">Total</p>
              <p className="text-2xl text-[#D4AF37] font-medium font-sans">₱{PACKAGE_PRICE.toLocaleString()}</p>
            </div>
          </div>
          <ul className="space-y-2.5 text-sm text-white/80 font-sans">
            {INCLUSIONS.map(line => (
              <li key={line} className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-[#1C7D3F] flex-shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 grid grid-cols-3 gap-3 pt-5 border-t border-white/5 text-center">
            <div><p className="text-xs text-[#D4AF37] font-sans">₱70-150</p><p className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-sans mt-0.5">per bottle</p></div>
            <div><p className="text-xs text-[#D4AF37] font-sans">₱200</p><p className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-sans mt-0.5">referral bonus</p></div>
            <div><p className="text-xs text-[#D4AF37] font-sans">Zero</p><p className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-sans mt-0.5">capital needed</p></div>
          </div>
        </section>

        {/* Payment method */}
        <section className="bg-[#141414] border border-white/10 p-6 sm:p-8 mb-6" data-testid="payment-method-section">
          <h3 className="text-sm uppercase tracking-[0.22em] text-white mb-4 font-sans">1. Choose Payment Method</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {methods.length === 0 && <p className="text-white/40 text-xs font-sans col-span-full">No payment methods configured. Please contact admin.</p>}
            {methods.map(([k, m]) => (
              <button
                key={k}
                type="button"
                onClick={() => setPaymentMethod(k)}
                className={`px-3 py-3 border text-xs uppercase tracking-[0.15em] font-sans transition-colors ${
                  paymentMethod === k
                    ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5'
                    : 'border-white/10 text-white/60 hover:border-white/30 hover:text-white'
                }`}
                data-testid={`pay-method-${k}`}
              >
                {m.name}
              </button>
            ))}
          </div>

          {selected && (
            <div className="mt-5 grid sm:grid-cols-2 gap-5 items-start">
              {selected.qr_image && (
                <div className="bg-white p-3 max-w-[260px] mx-auto sm:mx-0">
                  <img src={selected.qr_image} alt={`${selected.name} QR`} className="w-full" />
                </div>
              )}
              <div className="text-sm text-white/70 font-sans space-y-2">
                <p><span className="text-white/40">Account name:</span> <span className="text-white">{selected.account_name}</span></p>
                {selected.account_number && <p><span className="text-white/40">Account #:</span> <span className="text-white">{selected.account_number}</span></p>}
                <p className="text-[#D4AF37] font-medium pt-1">Send exactly ₱{PACKAGE_PRICE.toLocaleString()}</p>
              </div>
            </div>
          )}
        </section>

        {/* Proof upload */}
        <section className="bg-[#141414] border border-white/10 p-6 sm:p-8 mb-6" data-testid="proof-section">
          <h3 className="text-sm uppercase tracking-[0.22em] text-white mb-4 font-sans">2. Upload Payment Proof</h3>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Reference number (optional)"
            className="w-full bg-transparent border border-white/10 px-3 py-2.5 text-white text-sm font-sans focus:border-[#D4AF37] focus:outline-none mb-3"
            data-testid="payment-reference-input"
          />
          <label className="block w-full cursor-pointer">
            <div className={`border border-dashed ${proofUrl ? 'border-[#1C7D3F]/60 bg-[#1C7D3F]/5' : 'border-white/15'} flex items-center justify-center gap-2 py-5 px-4 text-sm font-sans hover:border-[#D4AF37]/40 transition-colors`}>
              {uploading ? (
                <><Sparkles className="w-4 h-4 text-[#D4AF37] animate-pulse" /><span className="text-white/60">Uploading…</span></>
              ) : proofUrl ? (
                <><CheckCircle2 className="w-4 h-4 text-[#1C7D3F]" /><span className="text-white">Proof uploaded — looking good</span></>
              ) : (
                <><Upload className="w-4 h-4 text-[#D4AF37]" /><span className="text-white/60">Tap to upload screenshot (max 6MB)</span></>
              )}
            </div>
            <input type="file" accept="image/*" onChange={handleProofUpload} className="hidden" data-testid="proof-upload-input" />
          </label>
          {proofUrl && (
            <div className="mt-3 max-w-[200px]">
              <img src={proofUrl} alt="Proof preview" className="w-full border border-white/10" />
            </div>
          )}
        </section>

        {/* Submit */}
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !paymentMethod || !proofUrl}
          className="w-full py-4 bg-[#D4AF37] text-black uppercase tracking-[0.22em] text-xs font-sans font-medium hover:bg-[#E5C158] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          data-testid="submit-tester-kit-btn"
        >
          {submitting ? 'Submitting…' : (<>Submit & Activate <ArrowRight className="w-3.5 h-3.5" /></>)}
        </button>

        {/* Reassurance */}
        <div className="mt-6 flex items-start gap-3 text-white/40 text-xs font-sans">
          <ShieldCheck className="w-4 h-4 text-[#1C7D3F] flex-shrink-0 mt-0.5" />
          <p>Payment is reviewed manually within 24 hours. Once approved, your dashboard auto-unlocks. <Link to="/refund-policy" className="underline hover:text-white">Refund policy</Link>.</p>
        </div>

        {/* Locked-state explainer */}
        <div className="mt-8 text-center">
          <Lock className="w-4 h-4 text-white/30 mx-auto mb-2" />
          <p className="text-white/30 text-[11px] uppercase tracking-[0.22em] font-sans">Dashboard locked until payment confirmed</p>
        </div>
      </div>
    </main>
  );
}
